import { useCallback, useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Search, Upload, UserPlus, DatabaseZap, ShieldCheck, Pencil, Trash2, MessageSquareText } from "lucide-react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PmAutopilot } from "@/components/PmAutopilot";
import { PmLeadDialog } from "@/components/PmLeadDialog";
import { DncStatus } from "@/components/DncScrub";

const TITLES = {
  owner: ["Owner Leads", "Property owners with for-lease-by-owner or expiring rental listings - pitch our property management service."],
  agent: ["Elite Rentals", "Real estate agents and brokers - pitch the Elite Rentals app as a free property management lead source."],
  fsbo: ["FSBO Leads", "Homeowners selling their home by themselves (for sale by owner) - offer your help as a licensed agent."],
  expired: ["Expired & Cancelled", "Homeowners whose listing came off the market without selling - offer a fresh plan to get it sold."],
};

const Row = ({ l, onEdit, onDelete }) => (
  <div className="grid gap-2 border-t border-slate-100 py-3 text-sm md:grid-cols-[2fr_2fr_1.4fr_auto]" data-testid={`pm-lead-${l.id}`}>
    <div><div className="font-semibold text-slate-900">{l.name}</div>
      <div className="text-xs text-slate-500">{l.listing_type || l.rank || l.source}{l.business ? ` · ${l.business}` : ""}</div>
      {l.property_address && <div className="text-xs text-slate-500">{l.property_address}</div>}</div>
    <div className="text-xs text-slate-600"><div>{l.phone || "No phone"}</div><div>{l.email || "No email"}{l.email_bounced ? " (bounced)" : ""}</div>
      {l.phone && <DncStatus l={l} testId={`pm-lead-dnc-${l.id}`} />}</div>
    <div className="flex flex-wrap items-start gap-1">
      {l.not_interested && <Badge className="border-0 bg-red-50 text-red-700">Not interested</Badge>}
      {l.call_status && <Badge className="border-0 bg-slate-100 text-slate-700">{l.call_status}</Badge>}
      {l.emails > 0 && <Badge className="border-0 bg-emerald-50 text-emerald-800">{l.emails} emails</Badge>}
      {l.postcard_status && <Badge className="border-0 bg-amber-50 text-amber-800">Postcard {l.postcard_status}</Badge>}
      {l.qr_scanned && <Badge className="border-0 bg-blue-50 text-blue-800">Scanned QR</Badge>}
    </div>
    <div className="flex gap-1">
      {l.phone && <Button asChild size="icon" variant="ghost" data-testid={`pm-lead-text-${l.id}`}><Link to={`/pm/texts?l=${l.id}`} aria-label="Text"><MessageSquareText className="h-4 w-4" /></Link></Button>}
      <Button size="icon" variant="ghost" onClick={() => onEdit(l)} data-testid={`pm-lead-edit-${l.id}`}><Pencil className="h-4 w-4" /></Button>
      <Button size="icon" variant="ghost" onClick={() => onDelete(l)} data-testid={`pm-lead-delete-${l.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
    </div>
  </div>
);

export default function PmLeads({ kind }) {
  const { user } = useAuth();
  const realtorKind = kind === "fsbo" || kind === "expired";
  if (user?.role === "realtor" && !realtorKind) return <Navigate to="/realtor" replace />;
  if (user?.role === "property_manager" && realtorKind) return <Navigate to="/pm" replace />;
  return <LeadsPage kind={kind} />;
}

function LeadsPage({ kind }) {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const file = useRef(null);
  const load = useCallback(() => api.get("/pm/leads", { params: { kind, search } }).then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e))), [kind, search]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const run = async (path, msg) => {
    try { const { data } = await api.post(path, { kind }); toast.success(`${msg} for ${data.count} leads - check back in a few minutes`); setTimeout(load, 15000); }
    catch (e) { toast.error(apiError(e)); }
  };
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await api.post(`/pm/leads/import?kind=${kind}`, fd); toast.success(`Added ${data.added}, ${data.duplicates} duplicates, ${data.skipped.length} skipped`); load(); }
    catch (err) { toast.error(apiError(err)); }
    e.target.value = "";
  };
  const del = async (l) => {
    if (!window.confirm(`Delete ${l.name}?`)) return;
    try { await api.delete(`/pm/leads/${l.id}`); toast.success("Lead deleted"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const [title, sub] = TITLES[kind];
  return (
    <div className="mx-auto max-w-6xl" data-testid={`pm-page-${kind}`}>
      <h1 className="font-display text-3xl font-extrabold text-slate-900">{title}</h1>
      <p className="mb-5 text-sm text-slate-600">{sub}</p>
      <PmAutopilot kind={kind} />
      <Card className="p-6">
        <div className="mb-4 flex flex-wrap gap-2">
          <div className="relative min-w-[220px] flex-1"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, phone, email, address..." className="pl-9" data-testid="pm-search" /></div>
          <Button variant="outline" onClick={() => run("/pm/leads/fill-contacts", "Filling in contact info")} data-testid="pm-fill-contacts"><DatabaseZap className="mr-2 h-4 w-4" />Fill missing contact info</Button>
          <Button variant="outline" onClick={() => run("/pm/leads/dnc-scrub", "Rechecking DNC")} data-testid="pm-dnc-recheck"><ShieldCheck className="mr-2 h-4 w-4" />Recheck DNC</Button>
          <Button variant="outline" onClick={() => file.current?.click()} data-testid="pm-import-csv"><Upload className="mr-2 h-4 w-4" />{kind === "fsbo" || kind === "expired" ? "Import MLS sheet" : "Import CSV"}</Button>
          <input ref={file} type="file" accept=".csv,.xlsx" className="hidden" onChange={upload} data-testid="pm-import-file" />
          <Button onClick={() => { setEdit(null); setOpen(true); }} data-testid="pm-add-lead"><UserPlus className="mr-2 h-4 w-4" />Add lead</Button>
        </div>
        <div className="text-xs text-slate-500" data-testid="pm-lead-count">{rows.length} leads · {kind === "fsbo" || kind === "expired" ? "CSV or Excel (MLS export). Owner name, address, city, zip, phone, email, MLS #, status, list price and expiration date are read automatically; missing owner names, phones and emails are looked up." : "CSV columns: name, phone, email, street, city, state, zip, property_address, notes"}</div>
        {rows.map((l) => <Row key={l.id} l={l} onEdit={(x) => { setEdit(x); setOpen(true); }} onDelete={del} />)}
        {!rows.length && <p className="py-8 text-center text-sm text-slate-500" data-testid="pm-empty">No leads yet. Turn on daily sourcing, import a CSV or add a lead.</p>}
      </Card>
      <PmLeadDialog open={open} onClose={() => setOpen(false)} lead={edit} kind={kind} onSaved={load} />
    </div>
  );
}
